import FormHeader from "@/components/FormHeader"
import { Button } from "@/components/ui/button"
import { Input } from "@/components/ui/input"
import { Label } from "@/components/ui/label"
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select"
import api from "@/lib/axios"
import { getListJalurJenis } from "@/services/getData"
import { JalurJenis } from "@/types"
import { AxiosError } from "axios"
import { Loader2 } from "lucide-react"
import { useState } from "react"
import toast from "react-hot-toast"
import { LuDownload } from "react-icons/lu"
import { RiFileExcel2Fill } from "react-icons/ri"
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query"

type FormImportSPK = {
  id_ref_jalur_jenis: string
  tipe_spk: string
  tahun: string
  file: File | null
}

const postData = async (newData: FormData) => {
  const response = await api.post("/master/spk/import", newData)
  return response.data
}

export default function FormImport({ closeModal }: { closeModal: () => void }) {
  const [form, setForm] = useState<FormImportSPK>({
    id_ref_jalur_jenis: "",
    tipe_spk: "",
    tahun: "",
    file: null,
  })
  const { data: dataJalurJenis } = useQuery({
    queryKey: ["listJalurJenis"],
    queryFn: getListJalurJenis,
  })

  const queryClient = useQueryClient()
  const { mutate, isPending } = useMutation({
    mutationFn: postData,
    onSuccess: (data) => {
      queryClient.invalidateQueries({ queryKey: ["listSpk"] })
      closeModal()
      toast.success(data?.message || "Success.")
    },
    onError: (error: AxiosError) => {
      const err = error as AxiosError<{ message?: string }>
      toast.error(
        err?.response?.data?.message || "An unexpected error occurred."
      )
    },
  })

  const handleSubmit = async (event: React.FormEvent<HTMLFormElement>) => {
    event.preventDefault()

    const formData = new FormData()
    formData.append("id_ref_jalur_jenis", form.id_ref_jalur_jenis)
    formData.append("tipe_spk", form.tipe_spk)
    formData.append("tahun", form.tahun)
    if (form.file) formData.append("file", form.file)

    mutate(formData)
  }

  return (
    <form onSubmit={handleSubmit} className="flex flex-col gap-y-3">
      <FormHeader title="Import Data SPK" icon={<RiFileExcel2Fill />} />

      <div>
        <Label>Jalur Jenis</Label>
        <Select
          value={form.id_ref_jalur_jenis}
          onValueChange={(value) =>
            setForm((prev) => ({
              ...prev,
              id_ref_jalur_jenis: value,
            }))
          }
        >
          <SelectTrigger>
            <SelectValue placeholder="Pilih Jalur Jenis">
              {form.id_ref_jalur_jenis &&
                dataJalurJenis?.find(
                  (jalurJenis: JalurJenis) =>
                    jalurJenis.id_ref_jalur_jenis === form.id_ref_jalur_jenis
                )?.nama_jalur_jenis}
            </SelectValue>
          </SelectTrigger>
          <SelectContent>
            {dataJalurJenis?.map((jalurJenis: JalurJenis, index: number) => (
              <SelectItem
                key={index}
                value={`${jalurJenis.id_ref_jalur_jenis}`}
              >
                {jalurJenis?.nama_jalur_jenis}
              </SelectItem>
            ))}
          </SelectContent>
        </Select>
      </div>
      <div>
        <Label>Tipe SPK</Label>
        <Select
          value={form.tipe_spk}
          onValueChange={(value) =>
            setForm((prev) => ({
              ...prev,
              tipe_spk: value as "spk1" | "spk2" | "spk3" | "spk4" | "spk5",
            }))
          }
        >
          <SelectTrigger>
            <SelectValue placeholder="Pilih Tipe SPK" />
          </SelectTrigger>
          <SelectContent>
            {["spk1", "spk2", "spk3", "spk4", "spk5"]?.map(
              (value: string, index: number) => (
                <SelectItem key={index} value={`${value}`}>
                  {value}
                </SelectItem>
              )
            )}
          </SelectContent>
        </Select>
      </div>
      <div>
        <Label>Tahun</Label>
        <Input
          required
          minLength={4}
          maxLength={4}
          placeholder="Ketik tahun..."
          value={form.tahun}
          onChange={(event) =>
            setForm((prev) => ({ ...prev, tahun: event.target.value }))
          }
        />
      </div>
      <div>
        <Label>Format Excel</Label>
        <div>
          <Button
            type="button"
            className="w-full flex items-center gap-3"
            variant={"outline"}
            onClick={() => {
              window.open("/format/format_import_spk.xlsx")
            }}
          >
            <LuDownload /> Unduh Format Import
          </Button>
        </div>
      </div>
      <div>
        <Label>File Excel</Label>
        <Input
          required
          type="file"
          onChange={(event) => {
            const files = event.target.files
            if (files && files[0]) {
              setForm((prev) => ({ ...prev, file: files[0] }))
            }
          }}
        />
      </div>

      <Button className="mt-3 flex items-center gap-3" disabled={isPending}>
        {isPending ? (
          <>
            <Loader2 className="w-4 h-4 mr-1 animate-spin" />
            Processing...
          </>
        ) : (
          "Simpan"
        )}
      </Button>
    </form>
  )
}
